import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../../shared/api/client';
import type { ReactNode } from 'react';
import { StepHeading } from '../../../shared/ui/StepHeading';
import { useWakeLock } from '../../../shared/lib/useWakeLock';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Send, Loader2, Type, Images } from 'lucide-react';
import { useAccountStore } from '../../../entities/account';
import { useTemplateStore } from '../../../entities/template';
import { postsApi } from '../../../shared/api/posts.api';
import { schedulePost } from '../../../shared/api/scheduler.api';
import { fromDateTimeLocal } from '../../../shared/lib/datetime-local';
import { ROUTES } from '../../../shared/config/routes';
import { submitPostJobServer } from '../../../shared/api/reels.api';
import { saveEnrichThemes } from '../../../features/statistics/api/account-report';
import { isBundleStale, STALE_BUNDLE_MESSAGE } from '../../../shared/lib/bundleFreshness';
import { toast } from 'sonner';
import { describeStoryFailures, type StoryOutcome } from '../../../shared/lib/storyFailures';
import { ExportTemplateButton, ExportZipButton } from '../../../features/template-export';
import { CarouselMediaUpload } from './CarouselMediaUpload';
import { MediaFile } from '../model/types';
import { usePostPreview } from '../lib/usePostPreview';
import { DEFAULT_PREVIEW_FONT_SIZE } from '../../../shared/lib/video';
import { usePublishStore } from '../model/usePublishStore';
import { uploadAllMedia } from '../lib/publishHelpers';
import { AccountsHorizontalList } from '../../../widgets/accounts-horizontal-list';
import type { Account } from '../../../entities/account';
import { GenerateCaptionsBulkButton, GenerateTitlesBulkButton } from '../../../features/caption-generate';
import {
  PublishConfirmationModal, WhenToPublish, accountsToPublish, missingForPost, missingText, montageReturn, parseScheduled, publishActionLabel, useIsDesktop,
  type AccountPublishStatus,
} from '../../../features/post-publish';
import type { PublicationCategory } from '../../../shared/types/common';
import { SELECTABLE_CATEGORIES, defaultCategory } from '../../../shared/lib/publicationCategories';
import { CollaboratorsInput } from '../../../features/collaborators-input';
import type { UserTag } from '../../../shared/lib/instagramTags';
import { mapIgError } from '../../../shared/lib/igError';
import { resolvePublishToast, type PublishResult } from '../../../shared/lib/publishToast';
import { savePublishResult, loadPublishResult, clearPublishResult } from '../../../shared/lib/publishResultStore';
import { bigFileWarning } from '../../../shared/lib/uploadWarning';
import { usePinchFontSize, haptic } from '../../../shared/lib/usePinchFontSize';
import { type PublishMode } from '../../../shared/lib/publishMode';
import { canPublishVia } from '../../../entities/account/lib/status';
import { FontSizeStepper } from '../../../shared/ui/FontSizeStepper';
import { useCurrentUser } from '../../../shared/hooks/useCurrentUser';
import {
  AccountGroupsBar, LastSetNotice, TextScope, WorkBriefToggle, reportPublished, saveLastAccounts, scopeTargets, useAccountGroups, useDraftAccounts,
  useLogsWork, useMontageItemAccount, usePublishDraft, workTitleFrom,
} from '../../../features/publish-groups';


/** Переключатель формата рисует страница — форма даёт ему место в своей колонке. */
interface CreateFormProps {
  headerSlot?: ReactNode;
}

export function CreatePostForm({ headerSlot }: CreateFormProps = {}) {
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState<string>('');
  // Описания — в общем черновике экрана: переживают переключение пост ↔ рилс.
  const captions = usePublishDraft((s) => s.captions);
  const setCaptions = usePublishDraft((s) => s.setCaptions);
  const setDraftHasMedia = usePublishDraft((s) => s.setHasMedia);
  const [titles, setTitles] = useState<Map<string, string>>(new Map());
  const [carouselMedia, setCarouselMedia] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishDone, setPublishDone] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [uploadWarning, setUploadWarning] = useState<string | null>(null);
  useWakeLock(publishing);
  const [previewMode, setPreviewMode] = useState<'simple' | 'template'>('template');
  const [fitMode, setFitMode] = useState<'fill' | 'fit'>('fill');
  // Ручной размер шрифта ПЕР-АККАУНТ (template-space px). Нет ключа = авто-фит шаблона.
  // У каждого аккаунта свой стиль → свой размер; подбираешь отдельно на превью.
  const [postFontSizes, setPostFontSizes] = useState<Map<string, number>>(new Map());
  // Что рендер подобрал сам и какой кегль ещё влезает в плашку (preview-space px).
  // Считает рендер, а не форма: тут нет ни канваса, ни загруженного шрифта.
  const [autoFontInfo, setAutoFontInfo] = useState<Map<string, { fontSize: number; max: number }>>(new Map());
  // Account tags per media slide (index-aligned to carouselMedia) and post-level collaborators.
  const [userTagsBySlide, setUserTagsBySlide] = useState<Map<number, UserTag[]>>(new Map());
  const [collaborators, setCollaborators] = useState<string[]>([]);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishStatuses, setPublishStatuses] = useState<AccountPublishStatus[]>([]);
  // Общий прогресс (как у рилса) — рисует процентный бар в модале.
  const [overallStatus, setOverallStatus] = useState<{
    phase: 'processing' | 'uploading' | 'remuxing' | 'publishing';
    current: number;
    total: number;
    percent: number;
    indeterminate?: boolean;
  } | null>(null);
  const [publicationCategory, setPublicationCategory] = useState<PublicationCategory | undefined>(
    () => defaultCategory(new URLSearchParams(window.location.search).has('takeItem')),
  );
  // Раздающая (Саяна) помечает пост для «Отработано» прямо здесь (26.10.2026).
  const logsWork = useLogsWork();
  const [withBrief, setWithBrief] = useState(true);
  // «Текст для: Русские» — заголовок и описание правятся у группы разом.
  const accountGroups = useAccountGroups();
  const [textScope, setTextScope] = useState<string | null>(null);
  const { userId } = useCurrentUser();
  // Отложенная публикация: строка datetime-local (локальное время), пусто = сразу.
  const [scheduledAt, setScheduledAt] = useState('');
  // batchId последнего пакета — по нему окно «Готово» найдёт ссылки на посты.
  const [lastBatchId, setLastBatchId] = useState<string | null>(null);
  // Ширина ≥ lg — превью справа; уже — превью в потоке формы, под аккаунтами.
  const isDesktop = useIsDesktop();
  const [previewOpen, setPreviewOpen] = useState(true);
  const [duplicateToStory, setDuplicateToStory] = useState(false);
  // ponytail: публикация только через API; телефонная логика сохранена, UI-переключатель убран
  const publishMode: PublishMode = 'api';
  const templateRef = useRef<HTMLDivElement>(null);
  const postPreviewRef = useRef<HTMLDivElement>(null);
  // Режим «менять размер пальцами»: рамка на превью + pinch вкл, тап-загрузка на паузе.
  const [fontEditActive, setFontEditActive] = useState(false);
  const prevSelectedTagsRef = useRef<Set<string>>(new Set());
  const logoInputRef = useRef<HTMLInputElement>(null);
  
  const accounts = useAccountStore((state) => state.accounts);
  const accountsLoading = useAccountStore((state) => state.isLoading);
  const accountsError = useAccountStore((state) => state.error);
  // Список аккаунтов зависит от выбранного способа: «телефон» → только phone-аккаунты
  // (залогинены в приложении), «API» → только graph/OAuth. Так при выборе режима
  // сразу выходит релевантный список телефона.
  const activeAccounts = accounts.filter(acc => acc.isActive && canPublishVia(acc, publishMode));
  const templates = useTemplateStore((state) => state.templates);
  const setPublishState = usePublishStore((state) => state.setPublishState);
  const resetPublishState = usePublishStore((state) => state.reset);

  // Пост из «Монтажа» (08.10.2026): ?montageTask&montageItem — картинки принятой
  // версии подставляются по порядку карусели, аккаунты направления первыми и
  // отмечены, ссылка на вышедший пост прикладывается к публикации сама.
  const [searchParams] = useSearchParams();
  const montageTask = searchParams.get('montageTask');
  const montageItem = searchParams.get('montageItem');
  // Ролик B2G у раздающей Редакции (08.10.2026) — тот же путь, другие адреса:
  // файл из «Роликов B2G», после публикации — отметка «Взяла» со ссылкой.
  const takeItem = searchParams.get('takeItem');
  const montageFrom = takeItem
    ? `/montage/takes/items/${encodeURIComponent(takeItem)}`
    : montageTask && montageItem
      ? `/montage/tasks/${encodeURIComponent(montageTask)}/items/${encodeURIComponent(montageItem)}`
      : null;
  const [montageAccountIds, setMontageAccountIds] = useState<string[]>([]);
  const [montageLoading, setMontageLoading] = useState(false);
  const [montageTitle, setMontageTitle] = useState<string | null>(null);
  const [montageSourceReady, setMontageSourceReady] = useState(false);
  const montageItemAccount = useMontageItemAccount(takeItem ? null : montageTask, takeItem ? null : montageItem, searchParams.get('montageAccount'));
  const returnTo = montageReturn({ montageTask, montageItem, takeItem });
  // Отмеченные аккаунты — общий черновик поста и рилса; набор по умолчанию
  // ставит хук: аккаунт публикации из «Монтажа» или «как в прошлый раз».
  const {
    checked: checkedAccountIds, setChecked: setCheckedAccountIds, lastIds: lastAccountIds, appliedLast, resetToEmpty, refreshLast,
  } = useDraftAccounts({
    userId,
    activeIds: activeAccounts.map((a) => a.id),
    montage: {
      active: !!montageFrom && !takeItem,
      resolved: montageSourceReady && montageItemAccount.resolved,
      itemAccountId: montageItemAccount.accountId,
      projectAccountIds: montageAccountIds,
    },
  });
  useEffect(() => {
    if (!montageFrom) return;
    let cancelled = false;
    setMontageLoading(true);
    // Пост из «Монтажа» уже свёрстан — шаблон поверх не накладываем.
    if (!takeItem) setPreviewMode('simple');
    (async () => {
      try {
        const source = await api.get<{ url: string; fileName: string; title: string; accountIds: string[]; images: { url: string | null; name: string }[] | null }>(
          `${montageFrom}/source`,
        );
        if (cancelled) return;
        setMontageAccountIds(source.accountIds);
        setMontageTitle(source.title);
        setMontageSourceReady(true);
        const list = source.images?.length ? source.images : [{ url: source.url, name: source.fileName }];
        const media = await Promise.all(list.map(async (img, i): Promise<MediaFile> => {
          if (!img.url) throw new Error(`картинка ${i + 1} недоступна`);
          const response = await fetch(img.url);
          if (!response.ok) throw new Error(`хранилище ответило ${response.status}`);
          const blob = await response.blob();
          const isVideo = blob.type.startsWith('video/');
          const file = new File([blob], img.name || `${i + 1}.jpg`, { type: blob.type || 'image/jpeg' });
          const preview = isVideo ? URL.createObjectURL(file) : await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error('не удалось прочитать картинку'));
            reader.readAsDataURL(file);
          });
          return { id: `montage-${i}-${Date.now()}`, file, preview, type: isVideo ? 'video' : 'image' };
        }));
        if (!cancelled) setCarouselMedia(media.slice(0, 10));
      } catch (error) {
        if (!cancelled) toast.error(`Не удалось взять пост из «Монтажа»: ${error instanceof Error ? error.message : 'ошибка'}`);
      } finally {
        if (!cancelled) setMontageLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [montageFrom]);
  // Пост из «Монтажа»: отмечен только его аккаунт (useDraftAccounts) — и он же в превью.
  useEffect(() => {
    const own = montageItemAccount.accountId;
    if (own && activeAccounts.some((a) => a.id === own)) setSelectedAccountId(own);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [montageItemAccount.accountId, accounts]);
  // Черновику экрана — только факт «медиа есть»: для подтверждения смены формата и ухода.
  useEffect(() => { setDraftHasMedia(carouselMedia.length > 0); }, [carouselMedia.length, setDraftHasMedia]);
  useEffect(() => () => setDraftHasMedia(false), [setDraftHasMedia]);

  // Получаем все уникальные тэги из активных аккаунтов
  const allTags = Array.from(
    new Set(
      activeAccounts.flatMap(acc => acc.tags || [])
    )
  ).sort();

  // Фильтруем аккаунты по выбранным тэгам и поисковому запросу
  let filteredAccounts = selectedTags.size > 0
    ? activeAccounts.filter(acc => 
        acc.tags?.some(tag => selectedTags.has(tag))
      )
    : activeAccounts;

  // Применяем поисковый фильтр
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase();
    filteredAccounts = filteredAccounts.filter(acc => 
      acc.name.toLowerCase().includes(query) || 
      acc.username?.toLowerCase().includes(query)
    );
  }

  // Сортируем аккаунты: с шаблонами первыми
  const sortedAccounts = [...filteredAccounts].sort((a, b) => {
    // Аккаунт самой публикации, за ним аккаунты направления из «Монтажа» — первыми.
    const aOwn = a.id === montageItemAccount.accountId;
    const bOwn = b.id === montageItemAccount.accountId;
    if (aOwn !== bOwn) return aOwn ? -1 : 1;
    const aDirection = montageAccountIds.includes(a.id);
    const bDirection = montageAccountIds.includes(b.id);
    if (aDirection !== bDirection) return aDirection ? -1 : 1;
    const aHasTemplate = templates.some(t => t.accountId === a.id);
    const bHasTemplate = templates.some(t => t.accountId === b.id);
    if (aHasTemplate && !bHasTemplate) return -1;
    if (!aHasTemplate && bHasTemplate) return 1;
    return 0;
  });

  // Обработчик клика по тэгу
  const handleTagClick = (tag: string) => {
    setSelectedTags(prev => {
      const newSet = new Set(prev);
      const isAdding = !newSet.has(tag);
      
      if (newSet.has(tag)) {
        newSet.delete(tag);
      } else {
        newSet.add(tag);
      }
      
      // Автоматически проставляем/снимаем чекбоксы
      setCheckedAccountIds(prevChecked => {
        const newChecked = new Set(prevChecked);
        
        // Находим все аккаунты с этим тегом
        const accountsWithTag = activeAccounts.filter(acc => acc.tags?.includes(tag));
        
        if (isAdding) {
          // Добавляем все аккаунты с этим тегом
          accountsWithTag.forEach(acc => newChecked.add(acc.id));
        } else {
          // Удаляем только те аккаунты, у которых нет других выбранных тегов
          accountsWithTag.forEach(acc => {
            const hasOtherSelectedTags = acc.tags?.some(t => t !== tag && newSet.has(t));
            if (!hasOtherSelectedTags) {
              newChecked.delete(acc.id);
            }
          });
        }
        
        return newChecked;
      });
      
      return newSet;
    });
  };

  // Получаем шаблон для выбранного аккаунта
  const selectedTemplateRaw = selectedAccountId
    ? templates.find(t => t.accountId === selectedAccountId && (t.type ?? 'reel') === 'post')
    : undefined;
  // Ручной размер шрифта выбранного аккаунта (если задан).
  const selectedFontOverride = postFontSizes.get(selectedAccountId);
  // Pre-publish размер выбранного аккаунта. Руками не выставили → отдаём
  // undefined, и рендер подбирает кегль сам под плашку этого паблика и под этот
  // заголовок. Раньше тут стояла зашитая цифра 24, поэтому дежурному приходилось
  // жать A+ заново на каждом посте и на каждом из пабликов.
  // Шаблонный fontSize по-прежнему не участвует: он пуст у 40 из 50 шаблонов.
  const selectedTemplate = selectedTemplateRaw
    ? { ...selectedTemplateRaw, fontSize: selectedFontOverride }
    : undefined;
  const selectedAutoInfo = autoFontInfo.get(selectedAccountId);
  // Базовый размер для +/- кнопок (template-space px): ручной, иначе подобранный.
  const baseFontSize = selectedFontOverride ?? selectedAutoInfo?.fontSize ?? DEFAULT_PREVIEW_FONT_SIZE;
  // Меняет размер шрифта ТОЛЬКО выбранного аккаунта (null = сброс на авто-подбор).
  const setSelectedFont = (v: number | null) => setPostFontSizes(prev => {
    const m = new Map(prev);
    if (v == null) m.delete(selectedAccountId); else m.set(selectedAccountId, v);
    return m;
  });
  // Рендер отвечает, каким кеглем нарисовал и что ещё влезает. Пишем только при
  // реальном изменении: иначе setState на каждом рендере закольцует превью.
  const handleResolvedFontSize = useCallback((info: { fontSize: number; max: number }) => {
    const id = selectedAccountId;
    if (!id) return;
    setAutoFontInfo(prev => {
      const cur = prev.get(id);
      if (cur && Math.abs(cur.fontSize - info.fontSize) < 0.5 && Math.abs(cur.max - info.max) < 0.5) return prev;
      const m = new Map(prev);
      m.set(id, info);
      return m;
    });
  }, [selectedAccountId]);
  // Pinch двумя пальцами в превью → размер шрифта выбранного аккаунта (мобилка).
  usePinchFontSize(postPreviewRef, {
    value: baseFontSize,
    min: 12,
    max: 200,
    onChange: (v) => setSelectedFont(v),
    enabled: fontEditActive && previewMode === 'template' && !!selectedTemplate && !!selectedAccountId,
  });

  // Получаем выбранный аккаунт для Instagram превью
  const selectedAccount = accounts.find(acc => acc.id === selectedAccountId);

  // Автоматически выбираем первый аккаунт
  useEffect(() => {
    // Если нет выбранного аккаунта или выбранный не в списке - выбираем первый
    if (sortedAccounts.length === 0) return; // Нет аккаунтов - выходим
    
    const isSelectedValid = selectedAccountId && sortedAccounts.some(acc => acc.id === selectedAccountId);
    
    if (!isSelectedValid) {
      console.log('🎯 Auto-selecting first account:', sortedAccounts[0].username || sortedAccounts[0].name);
      setSelectedAccountId(sortedAccounts[0].id);
    }
  }, [sortedAccounts, selectedAccountId]);

  // Пересчитываем publishStatuses при изменении checkedAccountIds
  useEffect(() => {
    if (!showPublishModal) return; // Только когда модалка открыта
    // Во время публикации и после её завершения статусы не трогаем — иначе
    // ре-рендеры (меняется ссылка filteredAccounts/captions) сбрасывали бы
    // success/error обратно в 'pending', и плитка не оставалась бы зелёной.
    // Как в ReelCreateForm. Финальные статусы сбросит handleAckDone при закрытии.
    if (publishing) return;
    if (publishDone) return;

    // Только отмеченные (из всех активных, не только из отфильтрованных поиском):
    // в аккаунт превью без галочки не публикуем.
    const statuses: AccountPublishStatus[] = accountsToPublish(activeAccounts, checkedAccountIds).map(account => ({
      account,
      status: 'pending' as const,
      caption: captions.get(account.id) || '',
    }));

    setPublishStatuses(statuses);
  }, [checkedAccountIds, activeAccounts, captions, showPublishModal, publishing, publishDone]);

  // Общий % по фазам (как у рилса): 0–90 = загрузка, 90–99 = публикация в Insta.
  // Публикация всегда стартует с 90% (current=0 → 90), дальше ползёт к 99 (крип).
  const calcPostPercent = (
    phase: 'uploading' | 'publishing',
    current: number, // uploading: 1-based; publishing: завершено (0-based count)
    total: number,
    withTemplate: boolean,
  ): number => {
    if (phase === 'uploading') {
      // С шаблоном грузим по аккаунту (N шагов) → 0–89. Без шаблона — одна общая
      // загрузка, держим середину пред-публикационного отрезка.
      if (!withTemplate) return 45;
      return Math.min(89, Math.round((current / total) * 89));
    }
    // publishing: 90 + доля завершённых аккаунтов, как у рилса (cap 99).
    return Math.min(99, 90 + Math.round((current / Math.max(1, total)) * 9));
  };

  // Крип фазы publishing: пока ждём Instagram, бар не висит, а ползёт к 99%.
  // Скопировано из ReelCreateForm. Math.max в обновлениях не даёт откатить назад.
  useEffect(() => {
    if (!overallStatus || overallStatus.phase !== 'publishing') return;
    if (overallStatus.percent >= 99) return;
    const startPercent = overallStatus.percent;
    const startedAt = Date.now();
    const DURATION_MS = 120_000;
    const TARGET = 99;
    const range = TARGET - startPercent;
    const id = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const targetPercent = Math.min(TARGET, startPercent + (elapsed / DURATION_MS) * range);
      setOverallStatus((prev) => {
        if (!prev || prev.phase !== 'publishing') return prev;
        const candidate = Math.round(targetPercent);
        return candidate > prev.percent ? { ...prev, percent: candidate } : prev;
      });
      if (elapsed >= DURATION_MS) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overallStatus?.phase]);
  
  // Получаем описание для выбранного аккаунта
  const caption = selectedAccountId ? (captions.get(selectedAccountId) || '') : '';

  // Получаем заголовок для выбранного аккаунта
  const title = selectedAccountId ? (titles.get(selectedAccountId) || '') : '';

  // Аккаунты для генерации - только когда есть выбранные чекбоксы
  const accountsForGeneration = checkedAccountIds.size > 0 
    ? filteredAccounts.filter(acc => checkedAccountIds.has(acc.id)).map(acc => ({ id: acc.id, name: acc.name }))
    : [];

  // Определяем тип поста автоматически
  const postType = carouselMedia.length === 1 ? 'single' : 'carousel';

  const handleCaptionsGenerated = (generatedCaptions: Map<string, string>) => {
    setCaptions(generatedCaptions);
  };

  const handleTitlesGenerated = (generatedTitles: Map<string, string>) => {
    setTitles(generatedTitles);
  };

  // Обработчик изменения заголовка
  const handleTitleChange = (newValue: string) => {
    if (!selectedAccountId) return;

    const scoped = scopeTargets(accountGroups, textScope, checkedAccountIds, selectedAccountId);
    if (scoped) {
      setTitles(prev => { const next = new Map(prev); scoped.forEach(id => next.set(id, newValue)); return next; });
      return;
    }

    // Получаем все значения заголовков активных аккаунтов
    const activeAccountValues = activeAccounts
      .map(acc => titles.get(acc.id) || '')
      .filter((val, idx, arr) => arr.indexOf(val) === idx); // Уникальные значения

    // Если у всех одинаковое значение (или Map пуста) - меняем у всех
    const allSame = activeAccountValues.length <= 1;

    if (allSame) {
      // Применяем ко всем активным аккаунтам
      const newTitles = new Map(titles);
      activeAccounts.forEach(acc => {
        newTitles.set(acc.id, newValue);
      });
      setTitles(newTitles);
    } else {
      // Меняем только у текущего аккаунта
      setTitles(prev => new Map(prev).set(selectedAccountId, newValue));
    }
  };

  // Обработчик изменения описания
  const handleCaptionChange = (newValue: string) => {
    if (!selectedAccountId) return;

    const scoped = scopeTargets(accountGroups, textScope, checkedAccountIds, selectedAccountId);
    if (scoped) {
      setCaptions(prev => { const next = new Map(prev); scoped.forEach(id => next.set(id, newValue)); return next; });
      return;
    }

    // Получаем все значения описаний активных аккаунтов
    const activeAccountValues = activeAccounts
      .map(acc => captions.get(acc.id) || '')
      .filter((val, idx, arr) => arr.indexOf(val) === idx); // Уникальные значения

    // Если у всех одинаковое значение (или Map пуста) - меняем у всех
    const allSame = activeAccountValues.length <= 1;

    if (allSame) {
      // Применяем ко всем активным аккаунтам
      const newCaptions = new Map(captions);
      activeAccounts.forEach(acc => {
        newCaptions.set(acc.id, newValue);
      });
      setCaptions(newCaptions);
    } else {
      // Меняем только у текущего аккаунта
      setCaptions(prev => new Map(prev).set(selectedAccountId, newValue));
    }
  };

  const handleAddCarouselMedia = (files: File[]) => {
    const remainingSlots = 10 - carouselMedia.length;
    const filesToAdd = files.slice(0, remainingSlots);

    filesToAdd.forEach((file) => {
      // Проверка типа файла
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      
      if (!isImage && !isVideo) {
        toast.error(`${file.name}: Поддерживаются только изображения и видео`);
        return;
      }

      // Проверка размера для видео (650MB для постов, как в Instagram)
      if (isVideo && file.size > 650 * 1024 * 1024) {
        toast.error(`${file.name}: Размер видео не должен превышать 650MB (лимит Instagram для постов)`);
        return;
      }

      // Проверка размера для изображений (можно оставить разумный лимит)
      if (isImage && file.size > 100 * 1024 * 1024) {
        toast.error(`${file.name}: Размер изображения не должен превышать 100MB`);
        return;
      }

      // Создаем превью
      const newMedia: MediaFile = {
        id: `${Date.now()}-${Math.random()}`,
        file,
        preview: isVideo ? URL.createObjectURL(file) : '', // Для видео используем createObjectURL
        type: isImage ? 'image' : 'video',
      };

      // Для изображений используем FileReader, для видео уже есть URL
      if (isImage) {
        const reader = new FileReader();
        reader.onloadend = () => {
          newMedia.preview = reader.result as string;
          setCarouselMedia((prev) => [...prev, newMedia]);
        };
        reader.readAsDataURL(file);
      } else {
        // Для видео сразу добавляем с createObjectURL
        setCarouselMedia((prev) => [...prev, newMedia]);
      }
    });

    if (filesToAdd.length < files.length) {
      toast.error(`Можно добавить максимум 10 файлов`);
    }
  };

  const handleRemoveCarouselMedia = (id: string) => {
    setCarouselMedia((prev) => prev.filter((m) => m.id !== id));
  };

  const handleAddCarouselMediaWithTemplate = async (files: File[]) => {
    // Просто добавляем медиа как обычно, без применения шаблона
    // Шаблон будет применяться только при публикации
    handleAddCarouselMedia(files);
  };

  // Используем hook для превью
  const { previewElement, canExport } = usePostPreview({
    onResolvedFontSize: handleResolvedFontSize,
    postType,
    previewMode,
    media: carouselMedia,
    template: selectedTemplate,
    account: selectedAccount,
    caption,
    title,
    templateRef,
    onImageUpload: handleAddCarouselMedia,
    onImageUploadWithTemplate: handleAddCarouselMediaWithTemplate,
    fitMode,
    userTags: userTagsBySlide.get(0) || [],
    onUserTagsChange: (next) => setUserTagsBySlide((prev) => new Map(prev).set(0, next)),
  });

  // Чего не хватает до публикации — и для строки под кнопкой, и для тоста.
  const checkedActive = accountsToPublish(activeAccounts, checkedAccountIds);
  const missing = missingForPost({ checkedCount: checkedActive.length, mediaCount: carouselMedia.length, hasCategory: !!publicationCategory });
  const hint = missingText(missing);
  const publishLabel = publishActionLabel({ count: checkedActive.length, scheduledAt: parseScheduled(scheduledAt) });

  // Синхронизируем состояние публикации с store. Кнопка не гаснет молча:
  // нажатие показывает, чего не хватает. Обработчик — через ref, иначе в
  // шапке остался бы снимок формы с прошлого рендера.
  const openPublishRef = useRef<() => void>(() => {});
  useEffect(() => {
    setPublishState({
      disabled: loading || publishing,
      loading: publishing,
      handlePublish: () => openPublishRef.current(),
      label: publishLabel,
      hint,
    });
  }, [loading, publishing, publishLabel, hint, setPublishState]);

  // Очищаем store при размонтировании
  useEffect(() => {
    return () => {
      resetPublishState();
    };
  }, [resetPublishState]);

  // Restore publish snapshot on mount (e.g. after page reload mid-publish)
  useEffect(() => {
    const snap = loadPublishResult();
    if (!snap || snap.kind !== 'post') return;
    setPublishStatuses(snap.accounts.map(a => ({
      account: { id: a.id ?? a.name, name: a.name, profilePicture: a.profilePicture } as Account,
      status: a.status, caption: '', error: a.status === 'error' ? a.friendly : undefined,
    })));
    if (snap.state === 'error') setPublishError(snap.errorReason ?? 'Публикация не удалась');
    else setPublishDone(true);
    setShowPublishModal(true);
  }, []);

  const handleOpenPublishModal = () => {
    if (missing.length > 0) {
      // Показываем, чего нет: тост со списком и прокрутка к первому полю.
      toast.error(hint);
      requestAnimationFrame(() => document.getElementById(missing[0].anchor)?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
      return;
    }

    // Только отмеченные аккаунты.
    const statuses: AccountPublishStatus[] = checkedActive.map(account => ({
      account,
      status: 'pending' as const,
      caption: captions.get(account.id) || '',
    }));

    setPublishStatuses(statuses);
    setShowPublishModal(true);
  };
  openPublishRef.current = handleOpenPublishModal;

  /** «Выбрать все по фильтру» / «Снять все». */
  const handleCheckMany = (ids: string[], checked: boolean) => {
    setCheckedAccountIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => (checked ? next.add(id) : next.delete(id)));
      return next;
    });
  };

  const handleAckDone = () => {
    setPublishDone(false);
    setPublishError(null);
    setUploadWarning(null);
    clearPublishResult();
    setShowPublishModal(false);
    setLastBatchId(null);
    setSelectedAccountId('');
    // Галочки остаются: следующий пост чаще всего в те же паблики
    // («Ещё одну в эти же аккаунты»). Отмеченный набор уже стал «последним».
    setCaptions(new Map());
    setScheduledAt('');
    setTitles(new Map());
    setCarouselMedia([]);
    setPublishStatuses([]);
    setPublicationCategory(defaultCategory(!!takeItem));
    setWithBrief(true);
    refreshLast();
    setTextScope(null);
    setUserTagsBySlide(new Map());
    setCollaborators([]);
  };

  const handleRetry = () => {
    setPublishError(null);
    setUploadWarning(null);
    clearPublishResult();
    setPublishStatuses(prev => prev.map(s => ({ ...s, status: 'pending' as const, error: undefined })));
    handleConfirmPublish();
  };

  // Phone-publish path (PUBLISH_MODE=phone): send raw carousel images + meta to
  // the backend /api/posts, which queues a phone-worker job (mediaType: 'photo').
  // SSE mirrors /api/reels, so the per-account onEvent handling matches the reel form.
  // v1 scope: photo + carousel + caption (NO templates/story/collaborators).
  const publishViaPhone = async () => {
    setPublishing(true);
    setUploadWarning(bigFileWarning(carouselMedia.reduce((n, m) => n + (m.file?.size ?? 0), 0)));

    const total = publishStatuses.length;
    const batchId = `batch-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const jobId = crypto.randomUUID();
    setOverallStatus({ phase: 'publishing', current: 0, total, percent: 5 });

    const accounts = publishStatuses.map(({ account }) => ({
      id: account.id,
      igUsername: account.username || account.name,
    }));
    const captionsRecord: Record<string, string> = {};
    publishStatuses.forEach(({ account }) => { captionsRecord[account.id] = captions.get(account.id) || ''; });

    let publishedCount = 0;
    const serverStatus = new Map<string, 'success' | 'error'>();
    const serverError = new Map<string, string>();
    let serverResults: { accountId: string; status: 'success' | 'error'; error?: string }[] = [];

    try {
      await submitPostJobServer({
        images: carouselMedia.map(m => m.file).filter(Boolean) as File[],
        meta: { batchId, jobId, accounts, captions: captionsRecord },
        onEvent: (event) => {
          if (event.phase === 'publish' && event.accountId) {
            const accId = event.accountId;
            if (event.status === 'success' || event.status === 'error') {
              // Terminal per-account outcome — advances the counter.
              publishedCount += 1;
              serverStatus.set(accId, event.status);
              if (event.status === 'success') {
                setPublishStatuses(prev => prev.map(s => s.account.id === accId ? { ...s, status: 'success' as const, stage: undefined } : s));
              } else {
                const friendly = mapIgError(event.error || 'Unknown error').friendly;
                serverError.set(accId, friendly);
                setPublishStatuses(prev => prev.map(s => s.account.id === accId ? { ...s, status: 'error' as const, error: friendly, stage: undefined } : s));
              }
              setOverallStatus(prev => {
                const percent = Math.min(100, 5 + Math.round((publishedCount / total) * 95));
                return { phase: 'publishing', current: publishedCount, total, percent: prev ? Math.max(prev.percent, percent) : percent };
              });
            } else if (event.stage) {
              // Phone-publish granular stage (no terminal status): keep tile
              // 'publishing' and surface the live stage.
              setPublishStatuses(prev => prev.map(s => s.account.id === accId ? { ...s, status: 'publishing' as const, stage: event.stage } : s));
            }
          }
          if (event.phase === 'done') serverResults = event.results || [];
          if (event.phase === 'error') throw new Error(`Server pipeline error: ${event.error}`);
        },
      });

      // Authoritative reconciliation from `done` — live events can be dropped.
      if (serverResults.length > 0) {
        setPublishStatuses(prev => prev.map(s => {
          const sr = serverResults.find(r => r.accountId === s.account.id);
          if (!sr) return s;
          return sr.status === 'success'
            ? { ...s, status: 'success' as const, stage: undefined }
            : { ...s, status: 'error' as const, stage: undefined, error: sr.error ? mapIgError(sr.error).friendly : s.error };
        }));
        serverResults.forEach(sr => {
          serverStatus.set(sr.accountId, sr.status);
          if (sr.status === 'error' && sr.error) serverError.set(sr.accountId, mapIgError(sr.error).friendly);
        });
      }

      const succ = publishStatuses.filter(s => serverStatus.get(s.account.id) === 'success').length;
      const allFailed = succ === 0;
      const firstErr = [...serverError.values()][0];
      savePublishResult({
        kind: 'post', state: allFailed ? 'error' : 'done',
        accounts: publishStatuses.map(s => ({
          id: s.account.id, name: s.account.name, profilePicture: s.account.profilePicture,
          status: serverStatus.get(s.account.id) === 'success' ? 'success' as const : 'error' as const,
          friendly: serverStatus.get(s.account.id) === 'error' ? serverError.get(s.account.id) : undefined,
          raw: undefined,
        })),
        successCount: succ, total,
        errorReason: allFailed ? firstErr : undefined, ts: Date.now(),
      });

      setPublishing(false);
      setOverallStatus(null);
      if (allFailed) setPublishError(firstErr ?? 'Публикация не удалась'); else setPublishDone(true);
    } catch (error) {
      console.error('Критическая ошибка при публикации (phone):', error);
      const friendly = error instanceof Error ? error.message : 'Не удалось опубликовать — проверьте подключение телефона';
      setPublishing(false);
      setOverallStatus(null);
      setPublishError(friendly);
      savePublishResult({
        kind: 'post', state: 'error',
        accounts: publishStatuses.map(s => ({
          id: s.account.id, name: s.account.name, profilePicture: s.account.profilePicture,
          status: 'error' as const, friendly, raw: undefined,
        })),
        successCount: 0, total, errorReason: friendly, ts: Date.now(),
      });
    }
  };

  // Второе касание «Опубликовать», пока первое ждёт проверку бандла, запускало
  // бы публикацию дважды — как у рилса, флаг ставится синхронно в ref.
  const publishStartRef = useRef(false);
  const handleConfirmPublish = async () => {
    if (publishStartRef.current) return;
    publishStartRef.current = true;
    try {
      await confirmPublishOnce();
    } finally {
      publishStartRef.current = false;
    }
  };

  const confirmPublishOnce = async () => {
    // Вкладка со старым бандлом рендерит по старым правилам (отзыв 04.09) —
    // публиковать из неё нельзя, только после перезагрузки.
    if (await isBundleStale()) {
      toast.error(STALE_BUNDLE_MESSAGE);
      return;
    }

    // Capability guard: skip accounts that cannot publish via the selected mode.
    const incompatible = publishStatuses.filter(s => !canPublishVia(s.account, publishMode));
    if (incompatible.length > 0) {
      toast.warning(`Пропущены (нельзя через ${publishMode === 'phone' ? 'телефон' : 'API'}): ${incompatible.map(s => '@' + (s.account.username || s.account.name)).join(', ')}`);
      // All selected accounts are incompatible → nothing to publish.
      if (incompatible.length === publishStatuses.length) return;
    }

    // Phone mode → :3001 /api/posts; API mode → existing edge-fn path below.
    if (publishMode === 'phone') {
      await publishViaPhone();
      return;
    }

    setPublishing(true);

    // Big-file warning before publishing
    const totalBytes = carouselMedia.reduce((n, m) => n + (m.file?.size ?? 0), 0);
    setUploadWarning(bigFileWarning(totalBytes));

    // Генерируем batchId для группировки публикаций
    const batchId = `batch-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setLastBatchId(batchId);

    // Hoisted so tail (after try/catch) can access results
    let results: Array<{ index: number; status: 'success' | 'error'; error?: string }> = [];
    // Hoisted: тоже нужен в tail (savePublishResult/done). Раньше был const внутри try
    // → ReferenceError в tail → done не выставлялся → модалка откатывалась в confirm.
    let mapped: Array<{ index: number; status: 'success' | 'error'; error?: string }> = [];

    const total = publishStatuses.length;
    const withTemplate = previewMode !== 'simple';
    setOverallStatus({ phase: 'uploading', current: 1, total, percent: calcPostPercent('uploading', 1, total, withTemplate) });

    try {
      // Шаг 1: Загружаем медиа
      const mediaUrls = new Map<string, Array<{ url: string; type: 'image' | 'video' }>>();

      // Режим "Без шаблона" - загружаем медиа один раз для всех
      if (previewMode === 'simple') {
        // Загружаем все медиа один раз
        const uploadedMedia = await uploadAllMedia(carouselMedia, {
          applyTemplateToFirst: false,
          template: undefined,
          title: '',
          templateRef,
          fitMode, // Передаем fitMode
        });

        // Сохраняем для использования всеми аккаунтами
        mediaUrls.set('common', uploadedMedia);
      } else {
        // Режим "С шаблоном" - загружаем для каждого аккаунта отдельно
        for (let i = 0; i < publishStatuses.length; i++) {
          const { account } = publishStatuses[i];

          // Обновляем статус - загрузка медиа
          setPublishStatuses(prev =>
            prev.map((s, idx) => idx === i ? { ...s, status: 'publishing' as const } : s)
          );

          try {
            const accountTitle = titles.get(account.id) || '';
            const accountTemplateRaw = templates.find(t => t.accountId === account.id && (t.type ?? 'reel') === 'post'); const _ovFs = postFontSizes.get(account.id); const accountTemplate = accountTemplateRaw ? { ...accountTemplateRaw, fontSize: _ovFs } : accountTemplateRaw;

            // Загружаем медиа с шаблоном для этого аккаунта
            const uploadedMedia = await uploadAllMedia(carouselMedia, {
              applyTemplateToFirst: !!accountTemplate,
              template: accountTemplate,
              title: accountTitle,
              templateRef,
              fitMode, // Передаем fitMode
            });

            mediaUrls.set(account.id, uploadedMedia);
            setOverallStatus({ phase: 'uploading', current: i + 1, total, percent: calcPostPercent('uploading', i + 1, total, true) });
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Неизвестная ошибка';
            setPublishStatuses(prev =>
              prev.map((s, idx) => idx === i ? { ...s, status: 'error' as const, error: errorMessage } : s)
            );
            // Продолжаем для других аккаунтов
            continue;
          }
        }
      }

      // Шаг 2: Создаём и публикуем посты параллельно
      let publishedCount = 0;
      const bumpPublishing = () => setOverallStatus(prev => {
        const p = calcPostPercent('publishing', publishedCount, total, withTemplate);
        const percent = prev ? Math.max(prev.percent, p) : p;
        return { phase: 'publishing', current: publishedCount, total, percent };
      });
      bumpPublishing(); // старт фазы публикации (current=0) → включает крип
      const montagePosted: { accountId: string; mediaId: string }[] = [];
      // Итог «Дублировать в сторис» по аккаунтам — предупредим разом после пакета.
      const storyOutcomes: { name: string; story?: StoryOutcome | null }[] = [];
      const publishPromises = publishStatuses.map(async ({ account }, index) => {
        try {
          // Получаем медиа: общие или специфичные для аккаунта
          const media = previewMode === 'simple'
            ? mediaUrls.get('common')
            : mediaUrls.get(account.id);

          // Проверяем что медиа загружены
          if (!media || media.length === 0) {
            return {
              index,
              status: 'error' as const,
              error: 'Медиа не загружены'
            };
          }

          const accountCaption = captions.get(account.id) || '';

          // Получаем шаблон для аккаунта
          const accountTemplateRaw = templates.find(t => t.accountId === account.id && (t.type ?? 'reel') === 'post'); const _ovFs = postFontSizes.get(account.id); const accountTemplate = accountTemplateRaw ? { ...accountTemplateRaw, fontSize: _ovFs } : accountTemplateRaw;

          // НЕ применяем uppercase к описанию!
          // textUppercase относится только к заголовку в шаблоне, не к caption
          const finalCaption = accountCaption;

          // Создаём пост с batchId
          const { post } = await postsApi.create({
            accountId: account.id,
            media,
            caption: finalCaption,
            batchId,
            category: publicationCategory, // Передаем выбранную категорию
            userTags: Array.from({ length: media.length }, (_, i) => userTagsBySlide.get(i) || []),
            collaborators,
          });

          // Отложенная публикация: пост уже создан со всем медиа, в срок его
          // опубликует планировщик бэкенда. Пусто — публикуем сразу.
          const runAt = fromDateTimeLocal(scheduledAt);
          if (runAt) {
            await schedulePost({
              postId: post.id,
              accountId: account.id,
              runAt,
              label: account.username || account.name,
              duplicateToStory,
            });
          } else {
            const published = await postsApi.publish(post.id, { duplicateToStory });
            if (published.post.instagramMediaId) montagePosted.push({ accountId: account.id, mediaId: published.post.instagramMediaId });
            if (duplicateToStory) storyOutcomes.push({ name: account.username || account.name, story: published.story });
            // Прогрев общего кэша отчёта: заголовок, напечатанный на кадре,
            // известен прямо сейчас — отчёту не придётся распознавать его
            // с картинки через GPT. Пишем ровно то, что видно на кадре
            // (с учётом капса шаблона); без шаблона на кадре текста нет.
            // Нормализация пробелов та же, что у распознанных тем: переносы
            // строк заголовка в отчёте становятся одиночными пробелами.
            const publishedTitle = (titles.get(account.id) ?? '').replace(/\s+/g, ' ').trim();
            const mediaIdForTheme = published.post.instagramMediaId;
            if (mediaIdForTheme && withTemplate && accountTemplate && publishedTitle) {
              void saveEnrichThemes([{
                id: mediaIdForTheme,
                theme: accountTemplate.textUppercase ? publishedTitle.toUpperCase() : publishedTitle,
              }]);
            }
          }

          // Загораем аккаунт зелёным СРАЗУ по готовности (по очереди, как в reels) —
          // не ждём общий батч в конце.
          setPublishStatuses(prev =>
            prev.map((s, idx) => idx === index ? { ...s, status: 'success' as const } : s)
          );
          publishedCount += 1;
          bumpPublishing();
          return { index, status: 'success' as const };
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Неизвестная ошибка';
          setPublishStatuses(prev =>
            prev.map((s, idx) => idx === index ? { ...s, status: 'error' as const, error: mapIgError(errorMessage).friendly } : s)
          );
          publishedCount += 1;
          bumpPublishing();
          return { index, status: 'error' as const, error: errorMessage };
        }
      });

      // Ждём завершения всех публикаций
      results = await Promise.all(publishPromises);
      // Пост уже в ленте; сторис, которые Instagram не принял, — отдельным предупреждением.
      const storyFail = describeStoryFailures(storyOutcomes);
      if (storyFail) toast.warning('Сторис вышли не везде', { description: storyFail, duration: 15000 });

      if (montagePosted.length) saveLastAccounts(userId, 'post', montagePosted.map((p) => p.accountId));
      // Пост из «Монтажа»: ссылку прикладываем сами — публикация закрыта.
      if (montageFrom && !takeItem) {
        const first = montagePosted.find((p) => montageAccountIds.includes(p.accountId)) ?? montagePosted[0];
        if (first) {
          api.post(`${montageFrom}/published-media`, first)
            .then(() => toast.success('Ссылка на пост приложена к публикации в «Монтаже»'))
            .catch((e) => toast.error(`Ссылку в «Монтаж» приложите руками: ${e instanceof Error ? e.message : 'ошибка'}`));
        }
      } else if (montagePosted.length) {
        // «Ролики B2G» и свои посты раздающей: ссылки всех пабликов — в учёт.
        const firstId = montagePosted[0].accountId;
        reportPublished({
          takeItem,
          posts: montagePosted,
          withBrief: logsWork ? withBrief : undefined,
          logsWork,
          title: montageTitle || workTitleFrom(titles.get(firstId) ?? '', captions.get(firstId) ?? ''),
        }).then((kind) => {
          if (kind === 'take') toast.success(`Отмечено в «Роликах B2G» — взяла, ссылок: ${montagePosted.length}`);
          if (kind === 'work') toast.success(`Записано в «Отработано» — ссылок: ${montagePosted.length}`);
        }).catch((e) => toast.error(`В «Отработано» не записалось — добавьте руками: ${e instanceof Error ? e.message : 'ошибка'}`));
      }

      // Обновляем статусы с friendly-mapped ошибками
      mapped = results.map(r => r.status === 'error' ? { ...r, error: mapIgError(r.error).friendly } : r);
      setPublishStatuses(prev => {
        const ns = [...prev];
        mapped.forEach(({ index, status, error }) => { ns[index] = { ...ns[index], status, error }; });
        return ns;
      });

    } catch (error) {
      console.error('Критическая ошибка при публикации:', error);
      const friendly = 'Не удалось загрузить медиа — проверьте интернет и попробуйте ещё раз';
      setPublishError(friendly);
      savePublishResult({
        kind: 'post', state: 'error',
        accounts: publishStatuses.map(s => ({
          id: s.account.id, name: s.account.name, profilePicture: s.account.profilePicture,
          status: 'error' as const, friendly, raw: undefined,
        })),
        successCount: 0, total: publishStatuses.length,
        errorReason: friendly, ts: Date.now(),
      });
    }

    setPublishing(false);
    setOverallStatus(null); // done — прячем бар, показываем баннер/плитки

    if (results.length > 0) {
      const toastResults: PublishResult[] = results.map(r => ({
        index: r.index, status: r.status,
        error: r.status === 'error' ? mapIgError(r.error).friendly : undefined,
      }));
      const succAll = toastResults.filter(r => r.status === 'success').length;
      const allFailedToast = succAll === 0 && toastResults.length > 0;
      // Full failure → modal only (no toast). Partial/success → toast as a dup.
      if (!allFailedToast) {
        const toastSpec = resolvePublishToast(toastResults);
        const description = 'description' in toastSpec ? toastSpec.description : undefined;
        if (toastSpec.variant === 'warning') toast.warning(toastSpec.message, description ? { description } : undefined);
        else if (toastSpec.variant === 'success') toast.success(toastSpec.message);
      }

      // Derive succ/allFailed/firstErr from `mapped` (which contains the post-update
      // friendly errors) joined with `publishStatuses` (pre-update, for account objects).
      // We cannot use `publishStatuses` alone here because the setPublishStatuses call
      // above is async and the closure variable is still the pre-update value for statuses,
      // but `mapped` already carries the correct final status+error for each index.
      const succ = mapped.filter(r => r.status === 'success').length;
      const allFailed = succ === 0 && mapped.length > 0;
      const firstErr = mapped.find(r => r.status === 'error')?.error;
      savePublishResult({
        kind: 'post', state: allFailed ? 'error' : 'done',
        accounts: publishStatuses.map((s, i) => {
          const r = mapped.find(m => m.index === i);
          return {
            id: s.account.id, name: s.account.name, profilePicture: s.account.profilePicture,
            status: r?.status === 'success' ? 'success' as const : 'error' as const,
            friendly: r?.status === 'error' ? r.error : undefined, raw: undefined,
          };
        }),
        successCount: succ, total: publishStatuses.length,
        errorReason: allFailed ? firstErr : undefined, ts: Date.now(),
      });
      if (allFailed) setPublishError(firstErr ?? 'Публикация не удалась'); else setPublishDone(true);
    }
  };

  const handleAccountCheck = (accountId: string, checked: boolean) => {
    setCheckedAccountIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(accountId);
      } else {
        newSet.delete(accountId);
      }
      return newSet;
    });
  };

  // Превью одно на форму: справа на широком экране, в потоке под аккаунтами —
  // на узком (два экземпляра сразу нельзя: превью сообщает форме кегль).
  const previewContent = (
        <div
          className={`relative space-y-2 rounded-lg ${fontEditActive ? 'ring-2 ring-primary ring-offset-2' : ''}`}
          ref={postPreviewRef}
          style={{ touchAction: fontEditActive ? 'none' : 'pan-y' }}
        >
          {previewElement}
          {/* Подсказка у текста (неактивно): тап → режим размера прямо в превью. */}
          {previewMode === 'template' && selectedTemplate && selectedAccountId && !!title.trim() && !fontEditActive && (
            <Button
              type="button"
              size="sm"
              onClick={() => setFontEditActive(true)}
              className="absolute bottom-3 left-1/2 z-20 -translate-x-1/2 rounded-full shadow"
            >
              <Type />
              Размер текста
            </Button>
          )}
          {fontEditActive && (
            <>
              {/* Блокируем тап-загрузку фото; pinch (2 пальца) проходит на обёртку. */}
              <div className="absolute inset-0 z-20 rounded-lg" />
              <Badge className="pointer-events-none absolute left-1/2 top-2 z-30 -translate-x-1/2 shadow">
                Двумя пальцами · {Math.round(baseFontSize)} px
              </Badge>
              <Button
                type="button"
                onClick={() => setFontEditActive(false)}
                className="absolute bottom-3 left-1/2 z-30 -translate-x-1/2 rounded-full px-8 shadow-lg"
              >
                Готово
              </Button>
            </>
          )}
        </div>
  );

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pb-28 lg:pb-0">
        {/* Левая колонка - форма */}
        <div className="space-y-6 lg:order-1">
          {headerSlot}
          {montageFrom && (
            <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-sm">
              {montageLoading ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <Images className="h-4 w-4 shrink-0" />}
              <span className="min-w-0">
                {montageLoading ? 'Беру картинки из «Монтажа»…' : 'Пост из «Монтажа»'}
                {montageTitle && <span className="text-muted-foreground"> — {montageTitle}</span>}
                {!montageLoading && !takeItem && montageSourceReady && montageItemAccount.resolved && checkedAccountIds.size === 0 && (
                  <span className="block text-xs text-muted-foreground">
                    {montageAccountIds.length === 0
                      ? 'У направления не указаны аккаунты — отметьте аккаунт вручную.'
                      : 'У публикации не выбран аккаунт — отметьте один: аккаунты направления стоят первыми.'}
                  </span>
                )}
              </span>
            </div>
          )}
          <StepHeading step={1} title="Куда публикуем" hint="Галочка — публикуем, тап — превью" />

          {/* Выбор аккаунта */}
          <div id="publish-accounts" className="space-y-2 scroll-mt-20">
            <Label>Аккаунты</Label>
            {/* Стор пуст и пока грузится ≠ «аккаунтов нет»: до фикса форма врала обоими случаями. */}
            {accountsLoading ? (
              <div className="px-2 py-6 text-center text-sm text-muted-foreground bg-muted rounded-lg">
                Загружаем аккаунты…
              </div>
            ) : activeAccounts.length === 0 ? (
              <div className="px-2 py-6 text-center text-sm text-muted-foreground bg-muted rounded-lg">
                {accountsError
                  ? 'Не удалось загрузить аккаунты — обновите страницу'
                  : 'Нет активных аккаунтов'}
              </div>
            ) : (
              <>
                {/* Поиск по аккаунтам */}
                <Input
                  type="text"
                  placeholder="Поиск аккаунта"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="mb-3"
                />

                {/* Фильтр по тэгам */}
                {allTags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {allTags.map(tag => (
                      <Badge
                        key={tag}
                        variant={selectedTags.has(tag) ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => handleTagClick(tag)}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
                
                <LastSetNotice
                  applied={appliedLast}
                  checked={checkedAccountIds}
                  lastIds={lastAccountIds}
                  availableIds={activeAccounts.map((a) => a.id)}
                  onReset={resetToEmpty}
                />
                <AccountGroupsBar
                  availableIds={activeAccounts.map((a) => a.id)}
                  checked={checkedAccountIds}
                  onChange={setCheckedAccountIds}
                  lastIds={lastAccountIds}
                />

                {filteredAccounts.length === 0 ? (
                  <div className="px-2 py-6 text-center text-sm text-muted-foreground bg-muted rounded-lg">
                    Ничего не нашлось
                  </div>
                ) : (
                  <AccountsHorizontalList
                    selectedAccountId={selectedAccountId}
                    onAccountSelect={(account: Account) => setSelectedAccountId(account.id)}
                    showCheckboxes={true}
                    onAccountCheck={handleAccountCheck}
                    onCheckMany={handleCheckMany}
                    checkedAccountIds={checkedAccountIds}
                    accounts={sortedAccounts}
                    allAccounts={activeAccounts}
                    accountsWithTemplates={new Set(templates.filter(t => (t.type ?? 'reel') === 'post').map(t => t.accountId))}
                  />
                )}
              </>
            )}
            {previewMode === 'template' && selectedAccountId && !selectedTemplateRaw && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-muted/60 px-3 py-2 text-sm">
                <span className="text-muted-foreground">У @{selectedAccount?.username || selectedAccount?.name} нет шаблона поста.</span>
                <Link className="font-medium underline" to={`${ROUTES.TEMPLATE_NEW}?accountId=${encodeURIComponent(selectedAccountId)}`}>
                  Создать шаблон для @{selectedAccount?.username || selectedAccount?.name}
                </Link>
                <button type="button" className="font-medium underline" onClick={() => setPreviewMode('simple')}>
                  Публиковать без шаблона
                </button>
              </div>
            )}
          </div>

          {/* Что публикуем: тип контента и сторис — рядом с «куда», а не в «когда». */}
          <div className="space-y-2">
            <Label htmlFor="content-type">
              Тип контента <span className="text-destructive">*</span>
            </Label>
            <Select
              value={publicationCategory || ''}
              onValueChange={(value) => setPublicationCategory(value as PublicationCategory)}
            >
              <SelectTrigger id="content-type" className="scroll-mt-20">
                <SelectValue placeholder="Выберите тип контента..." />
              </SelectTrigger>
              <SelectContent>
                {SELECTABLE_CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Дублировать в сторис */}
          <div className="flex items-center justify-between">
            <Label htmlFor="duplicate-story">Дублировать в сторис</Label>
            <Switch
              id="duplicate-story"
              checked={duplicateToStory}
              onCheckedChange={setDuplicateToStory}
            />
          </div>

          {/* Узкий экран: превью здесь, под выбором аккаунтов, и его можно свернуть. */}
          {!isDesktop && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-muted-foreground">
                  {carouselMedia.length ? `Файлов: ${carouselMedia.length}` : 'Превью'}
                </span>
                <Button type="button" size="sm" variant="ghost" onClick={() => setPreviewOpen((v) => !v)}>
                  {previewOpen ? 'Скрыть превью' : 'Показать превью'}
                </Button>
              </div>
              {previewOpen && previewContent}
            </div>
          )}

          <StepHeading step={2} title="Оформление и медиа" hint="Шаблон аккаунта, фото или карусель" />

          {/* Режим превью */}
          <div className="space-y-2">
            <Tabs value={previewMode} onValueChange={(v) => setPreviewMode(v as 'simple' | 'template')}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="template" disabled={!selectedTemplate}>
                  С шаблоном
                </TabsTrigger>
                <TabsTrigger value="simple">Без шаблона</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Загрузка медиа */}
          <div id="publish-media" className="scroll-mt-20">
            <CarouselMediaUpload
              mediaFiles={carouselMedia}
              onAddMedia={previewMode === 'template' ? handleAddCarouselMediaWithTemplate : handleAddCarouselMedia}
              onRemoveMedia={handleRemoveCarouselMedia}
            />
          </div>

          {/* Режим отображения медиа */}
          {carouselMedia.length > 0 && (
            <div className="space-y-2">
              <Label>Кадрирование</Label>
              <Tabs value={fitMode} onValueChange={(v) => setFitMode(v as 'fill' | 'fit')}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="fill" title="Медиа заполняет кадр, лишнее обрезается">
                    Заполнить
                  </TabsTrigger>
                  <TabsTrigger value="fit" title="Медиа видно целиком, по краям поля">
                    Вписать
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          )}

          {/* Размер шрифта поста — СВОЙ на каждый аккаунт (подбираешь на превью) */}
          {previewMode === 'template' && selectedTemplate && (
            <div className="space-y-2">
              <Label>Размер шрифта · {selectedAccount?.username || selectedAccount?.name}</Label>
              <FontSizeStepper
                value={baseFontSize}
                isAuto={selectedFontOverride == null}
                onChange={(delta) => {
                  haptic();
                  setSelectedFont(
                    Math.min(200, Math.max(12, Math.round(baseFontSize + delta))),
                  );
                }}
                onReset={() => setSelectedFont(null)}
              />
              {checkedAccountIds.size > 1 && (
                <p className="text-xs text-muted-foreground">
                  У каждого аккаунта размер свой — переключайте аккаунт на превью.
                </p>
              )}
            </div>
          )}

          {/* Заголовок (только для режима с шаблоном) */}
          {previewMode === 'template' && selectedTemplate && (
            <div className="space-y-2">
              <TextScope
                checked={checkedAccountIds}
                value={textScope}
                selectedId={selectedAccountId}
                onChange={(groupId, previewId) => { setTextScope(groupId); if (previewId) setSelectedAccountId(previewId); }}
              />
              <div className="flex items-center justify-between">
                <Label htmlFor="title">Заголовок</Label>
                <GenerateTitlesBulkButton
                  existingText={title}
                  accounts={accountsForGeneration}
                  onTitlesGenerated={handleTitlesGenerated}
                />
              </div>
              <Textarea
                id="title"
                placeholder="Текст на изображении..."
                value={title}
                onChange={(e) => handleTitleChange(e.target.value)}
                rows={2}
                maxLength={200}
              />
              <p className="text-right text-xs text-muted-foreground tabular-nums">
                {title.length}/200
              </p>
            </div>
          )}

          <StepHeading step={3} title="Текст публикации" />

          {/* Описание */}
          <div className="space-y-2">
            <TextScope
              checked={checkedAccountIds}
              value={textScope}
              selectedId={selectedAccountId}
              onChange={(groupId, previewId) => { setTextScope(groupId); if (previewId) setSelectedAccountId(previewId); }}
            />
            <div className="flex items-center justify-between">
              <Label htmlFor="caption">Описание</Label>
              <GenerateCaptionsBulkButton
                existingText={caption}
                accounts={accountsForGeneration}
                postType="post"
                onCaptionsGenerated={handleCaptionsGenerated}
              />
            </div>
            <Textarea
              id="caption"
              placeholder="Добавьте описание к посту..."
              value={caption}
              onChange={(e) => handleCaptionChange(e.target.value)}
              rows={4}
              maxLength={2200}
            />
            <p className="text-right text-xs text-muted-foreground tabular-nums">
              {caption.length}/2200
            </p>
          </div>

          {/* Соавторы — в тексте публикации, как у рилса (раньше жили под превью). */}
          <div className="space-y-2">
            <CollaboratorsInput value={collaborators} onChange={setCollaborators} />
          </div>

          <StepHeading step={4} title="Когда публикуем" />

          {/* Отложенная публикация: пост создаётся сразу, публикуется в срок */}
          <WhenToPublish id="post-scheduled-at" value={scheduledAt} onChange={setScheduledAt} />

          <WorkBriefToggle value={withBrief} onChange={setWithBrief} scheduled={!!scheduledAt} />


          {/* Кнопка экспорта для режима с шаблоном */}
          {canExport && selectedTemplate && (
            <>
              <ExportTemplateButton
                backgroundColor={selectedTemplate.backgroundColor}
                useGradient={selectedTemplate.useGradient}
                backgroundColorEnd={selectedTemplate.backgroundColorEnd}
                backgroundAlpha={selectedTemplate.backgroundAlpha}
                backgroundAlphaEnd={selectedTemplate.backgroundAlphaEnd}
                textColor={selectedTemplate.textColor}
                useTwoColors={selectedTemplate.useTwoColors}
                textColorSecondary={selectedTemplate.textColorSecondary}
                fontFamily={selectedTemplate.fontFamily}
                fontUrl={selectedTemplate.fontUrl}
                fontName={selectedTemplate.fontName}
                fontSize={selectedTemplate.fontSize}
                logoUrl={selectedTemplate.logoUrl}
                logoPosition={selectedTemplate.logoPosition}
                logoSize={selectedTemplate.logoSize}
                textLineSpacing={selectedTemplate.textLineSpacing}
                imageUrl={carouselMedia[0].preview}
                text={title}
                textUppercase={selectedTemplate.textUppercase}
                textAlign={selectedTemplate.textAlign}
                fontBold={selectedTemplate.fontBold}
                fitMode={fitMode}
                showLine={selectedTemplate.showLine}
                lineColor={selectedTemplate.lineColor}
                lineHeight={selectedTemplate.lineHeight}
                linePosition={selectedTemplate.linePosition}
                textShadowEnabled={selectedTemplate.applyFontEffects === false ? false : selectedTemplate.textShadowEnabled}
                textShadowColor={selectedTemplate.textShadowColor}
                textShadowBlur={selectedTemplate.textShadowBlur}
                textShadowWidth={selectedTemplate.textShadowWidth}
                textSoftShadowEnabled={selectedTemplate.applyFontEffects === false ? false : selectedTemplate.textSoftShadowEnabled}
                textSoftShadowColor={selectedTemplate.textSoftShadowColor}
                textSoftShadowBlur={selectedTemplate.textSoftShadowBlur}
                textSoftShadowOffsetX={selectedTemplate.textSoftShadowOffsetX}
                textSoftShadowOffsetY={selectedTemplate.textSoftShadowOffsetY}
                textSoftShadowOpacity={selectedTemplate.textSoftShadowOpacity}
                textShadowOpacity={selectedTemplate.textShadowOpacity}
                postLayout={selectedTemplate.postLayout}
                accentColor={selectedTemplate.accentColor}
                cardColor={selectedTemplate.cardColor}
                cardInset={selectedTemplate.cardInset}
                bandHeight={selectedTemplate.bandHeight}
                frameUrl={selectedTemplate.frameUrl}
                frameTextX={selectedTemplate.frameTextX}
                frameTextY={selectedTemplate.frameTextY}
                frameTextWidth={selectedTemplate.frameTextWidth}
                frameTextVAlign={selectedTemplate.frameTextVAlign}
                autoFontMax={selectedTemplate.autoFontMax}
                bandGrow={selectedTemplate.bandGrow}
                templateRef={templateRef}
                fileName={`instagram-post-${selectedAccountId}-${Date.now()}.png`}
                variant="outline"
                fullWidth
              />
              
              {/* Кнопка экспорта ZIP для всех выбранных аккаунтов */}
              <ExportZipButton
                checkedAccounts={filteredAccounts.filter(acc => checkedAccountIds.has(acc.id))}
                imageUrl={carouselMedia[0].preview}
                text={title}
                fitMode={fitMode}
                templateRef={templateRef}
                variant="outline"
                fullWidth
                fontSizeOverrides={postFontSizes}
              />
            </>
          )}
          
          {/* Сообщение о том что экспорт недоступен для видео */}
          {previewMode === 'template' && carouselMedia.length > 0 && selectedTemplate && carouselMedia[0].type === 'video' && (
            <div className="rounded-lg bg-muted p-3 text-center text-sm text-muted-foreground">
              Экспорт с шаблоном — только для изображений
            </div>
          )}
        </div>

      {/* Правая колонка - превью */}
      <div className="hidden lg:block space-y-4 lg:order-2 lg:sticky lg:top-[72px] lg:self-start lg:max-h-[calc(100vh-88px)] lg:overflow-y-auto">
        {isDesktop && previewContent}
      </div>
    </div>

      {/* Модалка подтверждения публикации */}
      <PublishConfirmationModal
        open={showPublishModal}
        onOpenChange={setShowPublishModal}
        accounts={publishStatuses}
        onConfirm={handleConfirmPublish}
        isPublishing={publishing}
        overallStatus={overallStatus ?? undefined}
        category={publicationCategory}
        done={publishDone}
        onAck={handleAckDone}
        errorState={!!publishError}
        errorReason={publishError ?? undefined}
        onRetry={handleRetry}
        uploadWarning={uploadWarning}
        scheduledAt={scheduledAt}
        duplicateToStory={duplicateToStory}
        batchId={lastBatchId}
        returnTo={returnTo}
        onPublishAgain={handleAckDone}
      />

      {/* Фиксированная кнопка внизу для мобильных */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 p-4 bg-background border-t shadow-lg z-50"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
        {hint && <p className="mb-2 text-center text-xs text-muted-foreground">{hint}</p>}
        <Button
          onClick={handleOpenPublishModal}
          disabled={loading || publishing}
          size="lg"
          className="w-full"
        >
          {publishing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Публикуем...
            </>
          ) : (
            <>
              <Send className="h-4 w-4" />
              {publishLabel}
            </>
          )}
        </Button>
      </div>
    </>
  );
}